import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const BLUE       = 'var(--signal)';
const BLUE_LIGHT = 'var(--signal-soft)';
const TEXT1      = 'var(--ink)';
const TEXT2      = 'var(--slate)';
const TEXT3      = 'var(--mute)';

function fmt(n) {
  return '$' + Math.round(n).toLocaleString();
}

// "2026-04-15" -> a Date at local midnight. The T00:00:00 suffix matches
// dateHelpers.js: without it the string parses as UTC and lands a day early
// west of GMT. Returns null when unparseable so callers can guard.
function parseLocalDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

// "2026-04-15" -> "Apr 15, 2026". Falls back to the raw string rather than
// blank if the date is unparseable.
function fmtDate(iso) {
  if (!iso) return '';
  const d = parseLocalDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : iso;
}

// "2026-04-15" -> "Apr 15". Year omitted because the running copy that uses
// this already names the tax year.
function fmtDateShort(iso) {
  const d = parseLocalDate(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : (iso ?? '');
}

// ["Sep 15", "Jan 15"] -> "Sep 15 and Jan 15" (or "…, … and …" past two).
function joinList(items, conjunction = 'and') {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} ${conjunction} ${items[items.length - 1]}`;
}

// Keyed off reserve_basis so the copy never has to infer which figure won by
// comparing prior_year_benchmark_quarterly against current_year_quarterly.
//
// This target is TruFlow's recommended cushion, deliberately set above the IRS
// legal minimum — it is NOT the §6654 safe harbor calculation. Copy must not
// promise penalty protection. See routers/tax.py::_compute_tax_reserve.
const RESERVE_BASIS_COPY = {
  prior_year_benchmark:  "That's based on last year's tax, which is higher than this year's projection — a deliberate cushion in case you earn more than expected.",
  current_year_estimate: "That's based on this year's projected income, which is running above last year's tax.",
};

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid var(--signal-soft)', borderTopColor: BLUE, animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function PageShell({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--paper)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
      boxSizing: 'border-box',
    }}>
      {children}
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      width: '100%',
      maxWidth: 420,
      backgroundColor: 'var(--card)',
      borderRadius: 'var(--r-card)',
      padding: 32,
      boxSizing: 'border-box',
      boxShadow: 'var(--sh-raise)',
    }}>
      {children}
    </div>
  );
}

const primaryButtonStyle = {
  width: '100%',
  backgroundColor: BLUE,
  color: 'var(--on-signal)',
  border: 'none',
  borderRadius: 10,
  padding: 14,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
};

function ScheduleRow({ quarter, date, isNext, isPast, last }) {
  // Only passed rows recede. Everything still ahead — next or not — reads at
  // full body weight, so the gone-by quarters are the ones that stand apart.
  // isNext wins over isPast in the edge case where the next unpaid quarter's
  // date has already gone by.
  const rowColor = !isNext && isPast ? TEXT3 : TEXT1;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 0',
      borderBottom: last ? 'none' : '0.5px solid rgba(0,0,0,0.07)',
    }}>
      <span style={{
        fontSize: 14,
        fontWeight: isNext ? 600 : 400,
        color: rowColor,
      }}>
        {quarter}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 14,
          fontWeight: isNext ? 600 : 400,
          color: rowColor,
        }}>
          {date}
        </span>
        {isNext && (
          <span style={{
            backgroundColor: BLUE_LIGHT,
            color: BLUE,
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 20,
          }}>
            Next
          </span>
        )}
      </span>
    </div>
  );
}

function TermlyFooter() {
  return (
    <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: 'var(--mute)' }}>
      <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=cd80ded6-7249-43fa-935c-0093b03f9dde"
         target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)', marginRight: 12 }}>
        Privacy Policy
      </a>
      <a href="https://app.termly.io/policy-viewer/policy.html?policyUUID=479a6457-8426-4afa-a7b1-f879c691825d"
         target="_blank" rel="noopener noreferrer" style={{ color: 'var(--mute)' }}>
        Terms of Service
      </a>
      <button
        className="termly-display-preferences"
        onClick={() => { if (window.displayPreferenceModal) window.displayPreferenceModal(); }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--mute)',
          fontSize: 11,
          marginLeft: 12,
          padding: 0,
          textDecoration: 'underline'
        }}
      >
        Consent Preferences
      </button>
    </div>
  );
}

// Pre-paywall onboarding summary. Shows the user the number they came for once,
// then hands off to /pricing.
//
// Fetches ONLY /tax/calculate — that endpoint is subscription-optional by
// design, so it resolves for an unsubscribed user mid-onboarding. /expenses and
// /cashflow/projection are require_active_access and would 402 here; do not add
// them to this screen.
export default function Summary() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [taxData, setTaxData] = useState(null);
  const [error, setError]     = useState(false);

  // Shared by the mount fetch and the error-state retry. isCancelled lets the
  // effect's cleanup drop a response that lands after unmount, so the mount
  // path never sets state on a gone component; the retry button passes nothing
  // and never cancels.
  const fetchSummary = useCallback(async (isCancelled = () => false) => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get('/tax/calculate');
      if (isCancelled()) return;
      setTaxData(res.data);
    } catch {
      if (isCancelled()) return;
      setError(true);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSummary(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchSummary]);

  if (loading) {
    return (
      <div style={{ backgroundColor: 'var(--paper)', minHeight: '100vh' }}>
        <Spinner />
      </div>
    );
  }

  if (error || !taxData) {
    return (
      <PageShell>
        <Card>
          <h1 style={{
            margin: '0 0 8px',
            fontFamily: 'var(--font-serif)',
            fontSize: 22,
            fontWeight: 700,
            color: TEXT1,
            textAlign: 'center',
            lineHeight: 1.25,
          }}>
            We couldn't load your numbers
          </h1>
          <p style={{
            margin: '0 0 24px',
            fontSize: 14,
            color: TEXT2,
            textAlign: 'center',
            lineHeight: 1.6,
          }}>
            Your setup is saved. This is a connection problem on our end — try again.
          </p>
          <button onClick={() => fetchSummary()} style={primaryButtonStyle}>
            Try again
          </button>
        </Card>
        <TermlyFooter />
      </PageShell>
    );
  }

  const schedule = Array.isArray(taxData.quarterly_schedule) ? taxData.quarterly_schedule : [];
  // No entry for "no_prior_year_data" — the caption and hold-back lines above
  // already say the number is an estimate off projected income. Null means no element.
  const reserveBasisCopy = RESERVE_BASIS_COPY[taxData.reserve_basis] ?? null;

  // Someone signing up mid-year has already missed some due dates, so the
  // reserve can't be described as a flat "every quarter". Split the schedule
  // on the browser's local calendar day — a due date landing today still
  // counts as payable.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const remaining = schedule.filter((q) => {
    const d = parseLocalDate(q.date);
    return d !== null && d >= today;
  });
  const passed = schedule.filter((q) => {
    const d = parseLocalDate(q.date);
    return d !== null && d < today;
  });

  // Q4 falls in January of the following year, so the tax year is Q1's year —
  // not the year of whichever date happens to come first in the array.
  const taxYearDate = parseLocalDate((schedule.find((q) => q.quarter === 'Q1') ?? schedule[0])?.date);
  const taxYear = taxYearDate ? taxYearDate.getFullYear() : null;

  // Only reframe when some dates have genuinely passed. A full four (or an
  // empty/unparseable schedule) keeps the original quarterly phrasing.
  const isPartialYear = remaining.length > 0 && remaining.length < 4;
  const reserveCaption = isPartialYear
    ? `${remaining.length} payment${remaining.length === 1 ? '' : 's'} left`
      + `${taxYear ? ` for your ${taxYear} taxes` : ''}`
      + ` — ${joinList(remaining.map((q) => fmtDateShort(q.date)))}.`
    : 'Set this aside each quarter';

  // The reserve is a quarter's worth, so a third of it is what each month
  // needs to hold back to get there.
  const monthlyHoldback = taxData.recommended_reserve / 3;

  // What the payments still ahead add up to, against the full-year bill. A
  // positive gap means earlier quarters carry tax the remaining payments
  // don't cover — but we can't know whether the user already paid them, so
  // the copy stays conditional.
  const covered = remaining.length * taxData.recommended_reserve;
  const shortfall = (taxData.total_annual_tax ?? 0) - covered;
  const showShortfall = isPartialYear && taxData.total_annual_tax != null && shortfall > 0;
  const passedLabel = joinList(passed.map((q) => q.quarter), 'or');
  // Reuses the split above — the rows never re-compare dates themselves.
  const passedDates = new Set(passed.map((q) => q.date));

  return (
    <PageShell>
      <Card>
        <p style={{
          margin: '0 0 2px',
          fontSize: 11,
          color: TEXT3,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          textAlign: 'center',
        }}>
          Your quarterly tax reserve
        </p>

        <p style={{
          margin: '4px 0 2px',
          fontFamily: 'var(--font-serif)',
          fontSize: 40,
          fontWeight: 700,
          color: TEXT1,
          lineHeight: 1.1,
          textAlign: 'center',
        }}>
          {fmt(taxData.recommended_reserve)}
        </p>

        <p style={{
          margin: 0,
          fontSize: 13,
          color: TEXT2,
          textAlign: 'center',
        }}>
          {reserveCaption}
        </p>

        <p style={{
          margin: '6px 0 0',
          fontSize: 13,
          fontWeight: 500,
          color: TEXT1,
          textAlign: 'center',
        }}>
          Hold back about {fmt(monthlyHoldback)} from each month's income.
        </p>

        {reserveBasisCopy && (
          <p style={{
            margin: '24px 0 0',
            fontSize: 14,
            color: TEXT1,
            textAlign: 'center',
            lineHeight: 1.6,
          }}>
            {reserveBasisCopy}
          </p>
        )}

        {schedule.length > 0 && (
          <div style={{
            border: '0.5px solid rgba(0,0,0,0.12)',
            borderRadius: 12,
            padding: '0 16px',
            margin: '24px 0 0',
          }}>
            {schedule.map((q, i) => (
              <ScheduleRow
                key={q.quarter ?? i}
                quarter={q.quarter}
                date={fmtDate(q.date)}
                isNext={q.is_next === true}
                isPast={passedDates.has(q.date)}
                last={i === schedule.length - 1}
              />
            ))}
          </div>
        )}

        {showShortfall && (
          <div style={{
            backgroundColor: BLUE_LIGHT,
            borderRadius: 12,
            padding: 14,
            margin: '16px 0 0',
            fontSize: 13,
            color: TEXT1,
            lineHeight: 1.6,
          }}>
            Your full-year tax comes to about {fmt(taxData.total_annual_tax)}, and the
            payments left don't cover all of it. If you haven't made your {passedLabel}{' '}
            payment{passed.length === 1 ? '' : 's'} yet, about {fmt(shortfall)} of that
            stays outstanding — it isn't due at the next quarterly deadline, it settles
            when you file.
          </div>
        )}

        <div style={{ marginTop: 28 }}>
          <button onClick={() => navigate('/pricing')} style={primaryButtonStyle}>
            See my plan
          </button>
        </div>

        <p style={{
          margin: '16px 0 0',
          fontSize: 11,
          color: TEXT3,
          textAlign: 'center',
          lineHeight: 1.6,
        }}>
          Estimates based on information you entered. TruFlow is not a tax advisor.
        </p>
      </Card>
      <TermlyFooter />
    </PageShell>
  );
}
